import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useMatches, useNavigate } from 'react-router-dom';

// material-ui
import { Box, Typography, IconButton, LinearProgress } from '@mui/material';

// assets
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useSelector } from 'store';
import { setBreadcrumbs } from 'slices/config';
import { useDispatch } from 'store';
import { currentItem } from 'slices/menu';
import LogoSection from 'components/logo';
import usePermissions from 'hooks/usePermissions';

const relationIds = ['lob', 'requisitionId', 'applicantId'];

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({ navigation, sx, guest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { current, permitted } = usePermissions();
  const breadcrumbs = useSelector((state) => state.config.breadcrumbs);
  const match = useMatches();

  const [goBack, setGoBack] = useState(false);

  const currentPath = location.pathname;
  const title = location.pathname
    .split('/')
    .pop()
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const handle = match.find((route) => route.handle !== undefined)?.handle;
  const breadcrumb = handle?.breadcrumb;
  const breadcrumbLoading = handle?.breadcrumbLoading;

  const showLogo = currentPath.startsWith('/interviews/requisition/');

  const hasQueryParams = location.search !== '' && relationIds.some((id) => location.search.includes(`${id}=`));

  const getCollapse = useCallback(
    (menu) => {
      if (menu.children) {
        menu.children.forEach((collapse) => {
          if (collapse.type === 'collapse') {
            getCollapse(collapse);
          } else if (collapse.type === 'item') {
            if (currentPath.includes(collapse.url)) {
              let lastPath = currentPath.split(collapse.url)?.at(-1);
              if (lastPath === '') {
                setGoBack(false);
                dispatch(currentItem({ currentItem: collapse.id }));
              } else {
                dispatch(currentItem({ currentItem: collapse.id }));
                setGoBack(true);
              }
              dispatch(setBreadcrumbs());
            }
          }
        });
      }
    },
    [currentPath, dispatch]
  );

  useEffect(() => {
    navigation?.items?.forEach((menu) => {
      if (menu.type === 'group') {
        getCollapse(menu);
      }
    });
  }, [navigation, currentPath, getCollapse]);

  let breadcrumbContent = (
    <Box
      id="breadcrumbs"
      display="flex"
      direction={'row'}
      justifyContent={'flex-start'}
      alignItems={'center'}
      spacing={1}
      sx={{
        pb: '20px',
        position: { xs: guest ? 'initial' : 'fixed', lg: 'initial' },
        width: { xs: 'auto', lg: '100%' },
        top: { xs: '22px', lg: 0 },
        left: { xs: '16px', lg: '-16px' },
        zIndex: 1199,
        marginTop: '4px',
        ...(permitted ? {} : { display: 'none' }),
        ...sx
      }}
    >
      {!breadcrumbs && breadcrumbLoading ? (
        <LinearProgress sx={{ width: '250px', ml: 1, my: 2 }} />
      ) : (
        <>
          {showLogo && <LogoSection to="#" keepATS={false} />}
          {current && (goBack || hasQueryParams) && (
            <IconButton onClick={() => navigate(-1)} size="small" disableRipple>
              <ArrowLeftOutlined style={{ fontSize: 16, stroke: 1.5, color: '#000000' }} />
            </IconButton>
          )}
          <Typography
            variant="h3"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maxWidth: 'calc(100vw - 120px)',
              textOverflow: 'ellipsis',
              ...(showLogo && { pl: 1, ml: 1, borderLeft: '3px solid #000' })
            }}
          >
            {breadcrumbs ?? breadcrumb ?? current?.navTitle ?? current?.title ?? title}
          </Typography>
        </>
      )}
    </Box>
  );

  return breadcrumbContent;
};

Breadcrumbs.propTypes = {
  navigation: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
};

export default Breadcrumbs;
